import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { ROUTES } from '@/constants/routes';
import { useCurrentUser } from '@/features/Profile/core/hooks';
import { formatDate, formatPhoneNumber, sleep } from '@/utils/helpers';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import {
  DESTROY_USER,
  DestroyUserResponse,
} from '@/features/Profile/sections/ProfileSettings/api';
import { MODAL_ANIMATION_DURATION } from '@/styles/constants';

function getRegistrationDate(createdAt?: string) {
  if (!createdAt) return '';

  return formatDate(createdAt, 'dd.MM.yyyy');
}

export function useSettingsSection() {
  const { t } = useTranslation();
  const { loading, user, isActiveShop } = useCurrentUser();
  const [destroyAccountError, setDestroyAccountError] = useState('');
  const [isEmailConfirmationOpen, toggleEmailConfirmation] =
    useToggleOpenState();
  const [isRemoveAccountOpen, toggleRemoveAccount] = useToggleOpenState();
  const [isRemoveAccountConfirmOpen, toggleRemoveAccountConfirm] =
    useToggleOpenState();
  const [isRemoveAccountErrorOpen, toggleRemoveAccountError] =
    useToggleOpenState();
  const [destroyUser, { loading: isDestroyingUser }] =
    useMutation<DestroyUserResponse>(DESTROY_USER);
  const isEmailConfirmationVisible =
    !!user.email && user.emailConfirmed === false;
  const isEmailConfirmed = !!user.email && !!user.emailConfirmed;

  async function openRemoveAccountConfirm() {
    toggleRemoveAccount(false);
    await sleep(MODAL_ANIMATION_DURATION);
    toggleRemoveAccountConfirm(true);
  }

  function showDestroyAccountError(message: string) {
    setDestroyAccountError(message);
    toggleRemoveAccountConfirm(false);
    toggleRemoveAccountError(true);
  }

  async function removeAccount() {
    try {
      const response = await destroyUser();
      const result = response.data?.destroyUser;

      if (!result) {
        showDestroyAccountError(t('common.something_went_wrong'));
        return;
      }

      if (result.errors.length) {
        showDestroyAccountError(result.errors[0].message);
        return;
      }

      if (!result.entity) {
        showDestroyAccountError(t('common.something_went_wrong'));
        return;
      }

      window.location.href = ROUTES.home;
    } catch {
      showDestroyAccountError(t('common.something_went_wrong'));
    }
  }

  return {
    user,
    phone: formatPhoneNumber(user.phone),
    registrationDate: getRegistrationDate(user.createdAt),
    destroyAccountError,
    hasEmailStatusIcon: isEmailConfirmationVisible || isEmailConfirmed,
    isDestroyingUser,
    isEmailConfirmationOpen,
    isEmailConfirmationVisible,
    isEmailConfirmed,
    isRemoveAccountOpen,
    isRemoveAccountConfirmOpen,
    isRemoveAccountErrorOpen,
    isRemoveAccountVisible: !loading && !isActiveShop,
    openEmailConfirmation: () => toggleEmailConfirmation(true),
    closeEmailConfirmation: () => toggleEmailConfirmation(false),
    openRemoveAccount: () => toggleRemoveAccount(true),
    closeRemoveAccount: () => toggleRemoveAccount(false),
    openRemoveAccountConfirm,
    closeRemoveAccountConfirm: () => toggleRemoveAccountConfirm(false),
    closeRemoveAccountError: () => toggleRemoveAccountError(false),
    removeAccount,
  };
}
